import React, { useMemo, useState } from 'react';
import { useFeatureFlag } from '../contexts/feature-flag-context';
import { useMemberships, useOrganization, usePermissions } from '../contexts/session-context';
import { INSIGHTS_FEATURE_FLAG } from '../kiss-clients/constants';
import setFilteredLinks from './sidenav-links';
import SidenavHeader from './sidenav-header';
import SidenavList from './sidenav-list';

interface Props {
  onNavClick?: () => void;
}

const sidenavStyles = {
  height: '100%',
  background: '#f7f7f7',
  transition: 'all .1s ease-in-out',
  borderRight: '1px solid #eee',
};

export const Sidenav: React.FC<Props> = React.memo(({ onNavClick }: Props) => {
  const permissions = usePermissions();
  const memberships = useMemberships();
  const organization = useOrganization();

  const sidenavClosed = localStorage.getItem('sidenavClosed') === 'true';
  const pocMode = localStorage.getItem('infographics-initial-poc');
  const isInfographicsInitialPoc = Boolean(pocMode);

  const isMultiAppEnabled = useFeatureFlag(INSIGHTS_FEATURE_FLAG, false);
  const links = useMemo(
    () => setFilteredLinks(permissions, isMultiAppEnabled, isInfographicsInitialPoc),
    [permissions, isMultiAppEnabled, isInfographicsInitialPoc],
  );
  const [isClosed, setIsClosed] = useState(sidenavClosed);

  const wrapper = document.querySelector('.header-wrapper');
  const sidebarFixed = document.querySelector<HTMLElement>('.sidenav-fixed');

  if (!isClosed && wrapper !== null) {
    wrapper.classList.remove('add-padding');
  }

  if (sidebarFixed) {
    sidebarFixed.style.display = 'none';
  }

  const handleToggle = () => {
    const closedStatus = (!isClosed).toString();

    if (wrapper !== null) {
      wrapper.classList.toggle('add-padding');
    } else if (onNavClick) {
      onNavClick();
    }

    localStorage.setItem('sidenavClosed', closedStatus);

    setIsClosed(closedStatus === 'true');
  };

  return (
    <div data-testing-id="sidenav-container" style={{ borderTop: '5px solid #eb1300' }}>
      <div style={{ ...sidenavStyles, width: isClosed ? 0 : 225 }}>
        <SidenavHeader
          toggleNav={handleToggle}
          closed={isClosed}
        />
        <div style={{ display: isClosed ? 'none' : 'block' }}>
          <SidenavList
            links={links}
            hasMemberships={memberships.length > 0}
            systemAppIds={{
              issue: organization.basicIssueResourceId ?? '',
              task: organization.basicTaskResourceId ?? '',
            }}
          />
        </div>
      </div>
    </div>
  );
});

export default Sidenav;
